import { useState } from 'react';
import styles from './Calculator.module.css';

type Mode = 'simple' | 'advanced';
type InterpretationLevel = 'high' | 'moderate' | 'low';
type ExampleKey = 'height' | 'yield' | 'protein' | 'days';

interface Interpretation {
  level: InterpretationLevel;
  category: string;
  description: string;
}

interface CalculationResult {
  vp: number;
  vg: number;
  va: number | null;
  ve: number;
  vgxe: number;
  h2Broad: number;
  h2Strict: number | null;
  vpFormula: string;
}

type HeritabilityKind = 'broad' | 'strict';

const EXAMPLES: Record<
  ExampleKey,
  {
    mode: Mode;
    kind: HeritabilityKind;
    name: string;
    values: {
      vg?: number;
      ve?: number;
      va?: number;
      vd?: number;
      vi?: number;
      vgxe?: number;
      veAdv?: number;
    };
  }
> = {
  height: {
    mode: 'advanced',
    kind: 'strict',
    name: 'Altura de Planta en Trigo',
    values: { va: 180, vd: 45, vi: 20, vgxe: 60, veAdv: 120 },
  },
  yield: {
    mode: 'simple',
    kind: 'broad',
    name: 'Rendimiento en Maiz',
    values: { vg: 2.4, vgxe: 0.8, ve: 9.8 },
  },
  protein: {
    mode: 'advanced',
    kind: 'strict',
    name: 'Contenido Proteico en Soja',
    values: { va: 2.4, vd: 0.6, vi: 0.2, vgxe: 0.3, veAdv: 1.8 },
  },
  days: {
    mode: 'simple',
    kind: 'broad',
    name: 'Dias a Floracion en Arroz',
    values: { vg: 45, vgxe: 3, ve: 12 },
  },
};

const formatNumber = (value: number, digits = 3): string => value.toFixed(digits);

const getInterpretation = (h2: number, kind: HeritabilityKind): Interpretation => {
  if (kind === 'broad') {
    if (h2 > 0.7) {
      return {
        level: 'high',
        category: 'H2 amplio Alto (> 0.70)',
        description:
          'La mayor parte de la variacion fenotipica se explica por causas geneticas en sentido amplio.',
      };
    }
    if (h2 >= 0.3) {
      return {
        level: 'moderate',
        category: 'H2 amplio Medio (0.30 - 0.70)',
        description:
          'Existe una contribucion genetica intermedia al fenotipo y la interpretacion debe acompañarse con buen control experimental.',
      };
    }
    return {
      level: 'low',
      category: 'H2 amplio Bajo (< 0.30)',
      description:
        'La contribucion genetica total al fenotipo es baja y el ambiente tiene un peso importante.',
    };
  }

  if (h2 > 0.5) {
    return {
      level: 'high',
      category: 'H2 estricto Alto (> 0.50)',
      description:
        'La seleccion individual sera muy efectiva porque la fraccion aditiva explica una proporcion alta del fenotipo.',
    };
  }
  if (h2 >= 0.2) {
    return {
      level: 'moderate',
      category: 'H2 estricto Medio (0.20 - 0.50)',
      description:
        'La respuesta a seleccion puede ser intermedia y conviene sostenerla con buena evaluacion de progenies.',
    };
  }
  return {
    level: 'low',
    category: 'H2 estricto Bajo (< 0.20)',
    description:
      'La respuesta a seleccion sera limitada y conviene reforzar la evaluacion en multiples ambientes o familias.',
  };
};

const parseRequiredNonNegative = (rawValue: string): number | null => {
  if (rawValue.trim() === '') {
    return null;
  }
  const parsed = Number(rawValue);
  if (Number.isNaN(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
};

const parseOptionalNonNegative = (rawValue: string): number | null => {
  if (rawValue.trim() === '') {
    return null;
  }
  const parsed = Number(rawValue);
  if (Number.isNaN(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
};

export const HeredabilidadCalculator: React.FC = () => {
  const [mode, setMode] = useState<Mode>('simple');
  const [error, setError] = useState<string>('');
  const [exampleMessage, setExampleMessage] = useState<string>('');
  const [selectionDiff, setSelectionDiff] = useState<string>('');
  const [units, setUnits] = useState<string>('');
  const [responseValue, setResponseValue] = useState<number | null>(null);

  const [simpleInputs, setSimpleInputs] = useState({
    vg: '',
    vgxe: '',
    ve: '',
  });
  const [advancedInputs, setAdvancedInputs] = useState({
    va: '',
    vd: '',
    vi: '',
    vgxe: '',
    veAdv: '',
  });
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = (): void => {
    setError('');
    setResponseValue(null);

    if (mode === 'simple') {
      const vg = parseRequiredNonNegative(simpleInputs.vg);
      const vgxe = parseRequiredNonNegative(simpleInputs.vgxe);
      const ve = parseRequiredNonNegative(simpleInputs.ve);

      if (vg === null || vgxe === null || ve === null) {
        setResult(null);
        setError('Ingrese valores validos y no negativos para VG, VGxE y VE.');
        return;
      }

      const vp = vg + vgxe + ve;
      if (vp === 0) {
        setResult(null);
        setError('La varianza fenotipica total debe ser mayor que cero.');
        return;
      }
      const h2Broad = vg / vp;

      setResult({
        vp,
        vg,
        va: null,
        ve,
        vgxe,
        h2Broad,
        h2Strict: null,
        vpFormula: `VP = VG + VGxE + VE = ${formatNumber(vg, 2)} + ${formatNumber(vgxe, 2)} + ${formatNumber(ve, 2)} = ${formatNumber(vp, 2)}`,
      });
      return;
    }

    const va = parseRequiredNonNegative(advancedInputs.va);
    const vdValue = parseOptionalNonNegative(advancedInputs.vd);
    const viValue = parseOptionalNonNegative(advancedInputs.vi);
    const vgxe = parseRequiredNonNegative(advancedInputs.vgxe);
    const ve = parseRequiredNonNegative(advancedInputs.veAdv);

    if (
      va === null ||
      vgxe === null ||
      ve === null ||
      (advancedInputs.vd.trim() !== '' && vdValue === null) ||
      (advancedInputs.vi.trim() !== '' && viValue === null)
    ) {
      setResult(null);
      setError('Ingrese valores validos y no negativos para VA, VD, VI, VGxE y VE.');
      return;
    }

    const vd = vdValue ?? 0;
    const vi = viValue ?? 0;
    const vg = va + vd + vi;
    const vp = vg + vgxe + ve;
    if (vp === 0) {
      setResult(null);
      setError('La varianza fenotipica total debe ser mayor que cero.');
      return;
    }
    const h2Broad = vg / vp;
    const h2Strict = va / vp;

    setResult({
      vp,
      vg,
      va,
      ve,
      vgxe,
      h2Broad,
      h2Strict,
      vpFormula: `VP = VG + VGxE + VE = (${formatNumber(va, 2)} + ${formatNumber(vd, 2)} + ${formatNumber(vi, 2)}) + ${formatNumber(vgxe, 2)} + ${formatNumber(ve, 2)} = ${formatNumber(vp, 2)}`,
    });
  };

  const handleCalculateResponse = (): void => {
    if (!result || result.h2Strict === null) {
      setError('La respuesta a seleccion requiere calcular h2 en sentido estricto.');
      return;
    }

    const s = Number(selectionDiff);
    if (Number.isNaN(s)) {
      setError('Ingrese un diferencial de seleccion valido.');
      return;
    }

    setResponseValue(result.h2Strict * s);
    setError('');
  };

  const handleLoadExample = (exampleKey: ExampleKey): void => {
    const example = EXAMPLES[exampleKey];
    setMode(example.mode);
    setResult(null);
    setResponseValue(null);
    setError('');

    if (example.mode === 'simple') {
      setSimpleInputs({
        vg: String(example.values.vg ?? ''),
        vgxe: String(example.values.vgxe ?? ''),
        ve: String(example.values.ve ?? ''),
      });
    } else {
      setAdvancedInputs({
        va: String(example.values.va ?? ''),
        vd: String(example.values.vd ?? ''),
        vi: String(example.values.vi ?? ''),
        vgxe: String(example.values.vgxe ?? ''),
        veAdv: String(example.values.veAdv ?? ''),
      });
    }

    setExampleMessage(
      `Ejemplo de sentido ${example.kind === 'broad' ? 'amplio (H2)' : 'estricto (h2 y H2)'} cargado: ${example.name}. Presione "Calcular Heredabilidad".`,
    );
  };

  const broadInterpretation = result ? getInterpretation(result.h2Broad, 'broad') : null;
  const strictInterpretation =
    result && result.h2Strict !== null ? getInterpretation(result.h2Strict, 'strict') : null;

  const safeDivisor = result?.vp && result.vp > 0 ? result.vp : null;
  const vgPercent = result && safeDivisor ? (result.vg / safeDivisor) * 100 : 0;
  const vaPercent = result && result.va !== null && safeDivisor ? (result.va / safeDivisor) * 100 : null;
  const vgxePercent = result && safeDivisor ? (result.vgxe / safeDivisor) * 100 : 0;
  const vePercent = result && safeDivisor ? (result.ve / safeDivisor) * 100 : 0;

  return (
    <div className={styles.calculatorContainer}>
      <div className={styles.heredabilidadBox}>
        <div className={styles.calculatorHeader}>
          <h2>Calculadora de Heredabilidad</h2>
          <p>
            Calcule heredabilidad en sentido amplio (H2) y estricto (h2) a partir de componentes
            de varianza.
          </p>
        </div>

        <div className={styles.modeSelector}>
          <button
            type="button"
            className={`${styles.modeButton} ${mode === 'simple' ? styles.active : ''}`}
            onClick={() => {
              setMode('simple');
              setResult(null);
              setResponseValue(null);
              setError('');
            }}
          >
            sentido amplio
          </button>
          <button
            type="button"
            className={`${styles.modeButton} ${mode === 'advanced' ? styles.active : ''}`}
            onClick={() => {
              setMode('advanced');
              setResult(null);
              setResponseValue(null);
              setError('');
            }}
          >
            sentido estricto
          </button>
        </div>

        {mode === 'simple' ? (
          <div className={styles.inputSection}>
            <h3>Sentido amplio - componentes de varianza</h3>
            <p className={styles.formulaHint}>
              Este modo estima solo H2. Con VG no es posible obtener la varianza aditiva necesaria
              para h2.
            </p>
            <div className={styles.inputRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="vg">
                  Varianza Genetica (V<sub>G</sub>)
                </label>
                <span className={styles.formulaHint}>Varianza atribuible a diferencias geneticas</span>
                <input
                  id="vg"
                  type="number"
                  step="0.01"
                  min="0"
                  value={simpleInputs.vg}
                  onChange={(event) =>
                    setSimpleInputs((prev) => ({ ...prev, vg: event.target.value }))
                  }
                  placeholder="Ej: 12.5"
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="vgxe">
                  Varianza de Interaccion Genotipo x Ambiente (V<sub>GxE</sub>)
                </label>
                <span className={styles.formulaHint}>
                  Use 0 si los datos provienen de un unico ambiente
                </span>
                <input
                  id="vgxe"
                  type="number"
                  step="0.01"
                  min="0"
                  value={simpleInputs.vgxe}
                  onChange={(event) =>
                    setSimpleInputs((prev) => ({ ...prev, vgxe: event.target.value }))
                  }
                  placeholder="Ej: 1.2"
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="ve">
                  Varianza Ambiental (V<sub>E</sub>)
                </label>
                <span className={styles.formulaHint}>Varianza debida a factores ambientales</span>
                <input
                  id="ve"
                  type="number"
                  step="0.01"
                  min="0"
                  value={simpleInputs.ve}
                  onChange={(event) =>
                    setSimpleInputs((prev) => ({ ...prev, ve: event.target.value }))
                  }
                  placeholder="Ej: 8.3"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.inputSection}>
            <h3>Sentido estricto - componentes detallados</h3>
            <p className={styles.formulaHint}>
              Los componentes usados para h2 tambien permiten calcular H2 en sentido amplio.
            </p>
            <div className={styles.inputRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="va">
                  Varianza Aditiva (V<sub>A</sub>)
                </label>
                <span className={styles.formulaHint}>Efectos promedio de sustitucion alelica</span>
                <input
                  id="va"
                  type="number"
                  step="0.01"
                  min="0"
                  value={advancedInputs.va}
                  onChange={(event) =>
                    setAdvancedInputs((prev) => ({ ...prev, va: event.target.value }))
                  }
                  placeholder="Ej: 9.2"
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="vd">
                  Varianza de Dominancia (V<sub>D</sub>)
                </label>
                <span className={styles.formulaHint}>Interacciones intra-locus</span>
                <input
                  id="vd"
                  type="number"
                  step="0.01"
                  min="0"
                  value={advancedInputs.vd}
                  onChange={(event) =>
                    setAdvancedInputs((prev) => ({ ...prev, vd: event.target.value }))
                  }
                  placeholder="Ej: 2.5"
                />
              </div>
            </div>
            <div className={styles.inputRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="vi">
                  Varianza Epistatica (V<sub>I</sub>)
                </label>
                <span className={styles.formulaHint}>Interacciones entre loci</span>
                <input
                  id="vi"
                  type="number"
                  step="0.01"
                  min="0"
                  value={advancedInputs.vi}
                  onChange={(event) =>
                    setAdvancedInputs((prev) => ({ ...prev, vi: event.target.value }))
                  }
                  placeholder="Ej: 0.8"
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="vgxeAdv">
                  Varianza de Interaccion Genotipo x Ambiente (V<sub>GxE</sub>)
                </label>
                <span className={styles.formulaHint}>
                  Use 0 si los datos provienen de un unico ambiente
                </span>
                <input
                  id="vgxeAdv"
                  type="number"
                  step="0.01"
                  min="0"
                  value={advancedInputs.vgxe}
                  onChange={(event) =>
                    setAdvancedInputs((prev) => ({ ...prev, vgxe: event.target.value }))
                  }
                  placeholder="Ej: 1.2"
                />
              </div>
            </div>
            <div className={styles.inputRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="veAdv">
                  Varianza Ambiental (V<sub>E</sub>)
                </label>
                <span className={styles.formulaHint}>Efectos ambientales</span>
                <input
                  id="veAdv"
                  type="number"
                  step="0.01"
                  min="0"
                  value={advancedInputs.veAdv}
                  onChange={(event) =>
                    setAdvancedInputs((prev) => ({ ...prev, veAdv: event.target.value }))
                  }
                  placeholder="Ej: 8.3"
                />
              </div>
            </div>
          </div>
        )}

        <button type="button" className={styles.calculateButton} onClick={handleCalculate}>
          Calcular Heredabilidad
        </button>

        {error && <div className={styles.errorBox}>{error}</div>}
        {exampleMessage && <div className={styles.infoBox}>{exampleMessage}</div>}

        <div className={styles.examplesSection}>
          <h3>Ejemplos rapidos</h3>
          <h4 className={styles.exampleGroupTitle}>Sentido amplio (H2)</h4>
          <div className={styles.exampleButtons}>
            <button type="button" className={styles.exampleButton} onClick={() => handleLoadExample('yield')}>
              Rendimiento en Maiz - H2 amplio
            </button>
            <button type="button" className={styles.exampleButton} onClick={() => handleLoadExample('days')}>
              Dias a Floracion en Arroz - H2 amplio
            </button>
          </div>
          <h4 className={styles.exampleGroupTitle}>Sentido estricto (h2 y H2)</h4>
          <div className={styles.exampleButtons}>
            <button type="button" className={styles.exampleButton} onClick={() => handleLoadExample('height')}>
              Altura de Planta en Trigo - h2 estricto
            </button>
            <button type="button" className={styles.exampleButton} onClick={() => handleLoadExample('protein')}>
              Contenido Proteico en Soja - h2 estricto
            </button>
          </div>
        </div>

        {result && (
          <div className={styles.resultsSection}>
            <div className={styles.resultCard}>
              <h4>
                Varianza Fenotipica Total (V<sub>P</sub>)
              </h4>
              <div className={styles.resultValue}>{formatNumber(result.vp)}</div>
              <p className={styles.resultFormula}>{result.vpFormula}</p>
            </div>

            <div className={styles.resultCard}>
              <h4>Heredabilidad en Sentido Amplio (H2)</h4>
              <div className={styles.resultValue}>{formatNumber(result.h2Broad)}</div>
              <p className={styles.resultFormula}>
                H2 = V<sub>G</sub> / V<sub>P</sub>
              </p>
              {broadInterpretation && (
                <div
                  className={`${styles.resultInterpretation} ${styles[broadInterpretation.level]}`}
                >
                  <strong>{broadInterpretation.category}</strong>
                  <p>{broadInterpretation.description}</p>
                </div>
              )}
            </div>

            {result.h2Strict !== null && strictInterpretation && (
              <div className={styles.resultCard}>
                <h4>Heredabilidad en Sentido Estricto (h2)</h4>
                <div className={styles.resultValue}>{formatNumber(result.h2Strict)}</div>
                <p className={styles.resultFormula}>
                  h2 = V<sub>A</sub> / V<sub>P</sub>
                </p>
                <div
                  className={`${styles.resultInterpretation} ${styles[strictInterpretation.level]}`}
                >
                  <strong>{strictInterpretation.category}</strong>
                  <p>{strictInterpretation.description}</p>
                </div>
              </div>
            )}

            <div className={styles.resultCard}>
              <h4>Composicion de Varianza Fenotipica</h4>
              <div className={styles.varianceChart}>
                <div className={styles.varianceBar}>
                  <div className={styles.varianceLabel}>
                    V<sub>G</sub> (Genetica)
                  </div>
                  <div className={styles.varianceProgress}>
                    <div className={styles.varianceFill} style={{ width: `${vgPercent}%` }}>
                      {formatNumber(vgPercent, 1)}%
                    </div>
                  </div>
                </div>

                {vaPercent !== null && (
                  <div className={styles.varianceBar}>
                    <div className={styles.varianceLabel}>
                      V<sub>A</sub> (Aditiva)
                    </div>
                    <div className={styles.varianceProgress}>
                      <div
                        className={`${styles.varianceFill} ${styles.varianceFillBlue}`}
                        style={{ width: `${vaPercent}%` }}
                      >
                        {formatNumber(vaPercent, 1)}%
                      </div>
                    </div>
                  </div>
                )}

                <div className={styles.varianceBar}>
                  <div className={styles.varianceLabel}>
                    V<sub>GxE</sub> (G x Ambiente)
                  </div>
                  <div className={styles.varianceProgress}>
                    <div
                      className={`${styles.varianceFill} ${styles.varianceFillPurple}`}
                      style={{ width: `${vgxePercent}%` }}
                    >
                      {formatNumber(vgxePercent, 1)}%
                    </div>
                  </div>
                </div>

                <div className={styles.varianceBar}>
                  <div className={styles.varianceLabel}>
                    V<sub>E</sub> (Ambiental)
                  </div>
                  <div className={styles.varianceProgress}>
                    <div
                      className={`${styles.varianceFill} ${styles.varianceFillOrange}`}
                      style={{ width: `${vePercent}%` }}
                    >
                      {formatNumber(vePercent, 1)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {result.h2Strict !== null && (
              <div className={styles.responseCalculator}>
              <h4>Prediccion de Respuesta a Seleccion</h4>
              <p className={styles.responseSubtitle}>Calculo de ganancia genetica: R = h2 x S</p>
              <div className={styles.inputRow}>
                <div className={styles.inputGroup}>
                  <label htmlFor="selectionDiff">Diferencial de Seleccion (S)</label>
                  <input
                    id="selectionDiff"
                    type="number"
                    step="0.1"
                    value={selectionDiff}
                    onChange={(event) => setSelectionDiff(event.target.value)}
                    placeholder="Ej: 5.0"
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label htmlFor="units">Unidades</label>
                  <input
                    id="units"
                    type="text"
                    value={units}
                    onChange={(event) => setUnits(event.target.value)}
                    placeholder="Ej: kg/ha, cm, %"
                  />
                </div>
              </div>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={handleCalculateResponse}
              >
                Calcular Respuesta
              </button>
              {responseValue !== null && (
                <div className={styles.responseResult}>
                  <h5>Respuesta Esperada (R)</h5>
                  <div className={styles.responseValue}>
                    {formatNumber(responseValue, 2)} {units}
                  </div>
                  <p>
                    R = {result.h2Strict.toFixed(3)} x {Number(selectionDiff).toFixed(2)} ={' '}
                    {responseValue.toFixed(2)}
                  </p>
                </div>
              )}
              </div>
            )}
          </div>
        )}

        <div className={styles.formulaBox}>
          <h4>Formulas utilizadas</h4>
          <div className={styles.formulaItem}>
            <strong>Varianza Fenotipica:</strong> V<sub>P</sub> = V<sub>G</sub> + V<sub>GxE</sub> + V<sub>E</sub>
          </div>
          <div className={styles.formulaItem}>
            <strong>Varianza Genetica:</strong> V<sub>G</sub> = V<sub>A</sub> + V<sub>D</sub> + V<sub>I</sub>
          </div>
          <div className={styles.formulaItem}>
            <strong>Heredabilidad Sentido Amplio:</strong> H2 = V<sub>G</sub> / V<sub>P</sub>
          </div>
          <div className={styles.formulaItem}>
            <strong>Heredabilidad Sentido Estricto:</strong> h2 = V<sub>A</sub> / V<sub>P</sub>
          </div>
          <div className={styles.formulaItem}>
            <strong>Respuesta a Seleccion:</strong> R = h2 x S
          </div>
        </div>
      </div>
    </div>
  );
};
